import type { PrV2CommentLifecycle } from '@/lib/pr-v2/comment-lifecycle';
import type {
  PrV2CommentResponse,
  PrV2DiscussionResponse,
  PrV2ReviewResponse,
  PrV2ReviewThread,
} from '@/lib/pr-v2/types';

import type { PendingComments } from '../pending-comments-types';

export type PrV2LocalCommentLifecycle = PrV2CommentLifecycle;

export const PR_V2_LOCAL_COMMENT_LIFECYCLES = {
  queued: 'queued',
  submitting: 'submitting',
  persisted: 'persisted',
} as const;

export type PrV2LocalThreadLifecycle = 'submitting' | 'persisted';

export const PR_V2_LOCAL_THREAD_LIFECYCLES = {
  submitting: 'submitting',
  persisted: 'persisted',
} as const;

type PrV2LocalEntityBase = {
  clientId: string;
  lookupIds: string[];
};

export const PR_V2_LOCAL_SCOPE_ID_START = 1;

/**
 * PR-scoped local entity graph for PR v2 comments/reviews/threads.
 *
 * React Query still owns server snapshots and invalidation, but this store owns the UI-visible
 * entity identity and lifecycle. That lets queued, submitting, and persisted comments survive
 * source handoffs without components having to reconcile raw pending ids, optimistic ids, and
 * canonical GitHub ids themselves.
 */
export type PrV2LocalCommentEntity = PrV2LocalEntityBase & {
  comment: PrV2CommentResponse;
  lifecycle: PrV2LocalCommentLifecycle;
};

export type PrV2LocalReviewEntity = PrV2LocalEntityBase & {
  review: PrV2ReviewResponse;
};

export type PrV2LocalThreadEntity = PrV2LocalEntityBase & {
  thread: PrV2ReviewThread;
  lifecycle: PrV2LocalThreadLifecycle;
  optimisticResolvedOverride: boolean | null;
};

export type PrV2LocalScopeState = {
  nextCommentId: number;
  nextReviewId: number;
  nextThreadId: number;
  commentsByClientId: Record<string, PrV2LocalCommentEntity>;
  commentAliasToClientId: Record<string, string>;
  commentOrder: string[];
  reviewsByClientId: Record<string, PrV2LocalReviewEntity>;
  reviewAliasToClientId: Record<string, string>;
  reviewOrder: string[];
  threadsByClientId: Record<string, PrV2LocalThreadEntity>;
  threadAliasToClientId: Record<string, string>;
  threadOrder: string[];
};

export type PrV2LocalCommentReadModelComment = {
  clientId: string;
  comment: PrV2CommentResponse;
  lifecycle: PrV2LocalCommentLifecycle;
  displayId: string;
  parentDisplayId: string | null;
  threadId: string;
};

export type PrV2LocalCommentModelView = {
  comments: PrV2CommentResponse[];
  reviewThreads: PrV2ReviewThread[];
  commentReadModel: PrV2LocalCommentReadModelComment[];
  commentByLookupId: Map<string, PrV2CommentResponse>;
  readModelByLookupId: Map<string, PrV2LocalCommentReadModelComment>;
  commentByGithubId: Map<string, PrV2LocalCommentReadModelComment>;
  reviewByLookupId: Map<string, PrV2ReviewResponse>;
  reviewThreadByCommentNodeId: Map<string, PrV2ReviewThread>;
};

export type PrV2LocalCommentModelState = {
  scopes: Record<string, PrV2LocalScopeState>;
  syncPendingComments: (scopeKey: string, pendingComments: PendingComments[]) => void;
  reconcileComments: (scopeKey: string, comments: PrV2CommentResponse[]) => void;
  reconcileReviews: (scopeKey: string, reviews: PrV2ReviewResponse[]) => void;
  reconcileDiscussion: (
    scopeKey: string,
    timelineItems: PrV2DiscussionResponse['timelineItems'],
  ) => void;
  reconcileThreads: (scopeKey: string, reviewThreads: PrV2ReviewThread[]) => void;
  stageOptimisticComments: (scopeKey: string, comments: PrV2CommentResponse[]) => void;
  stageOptimisticReviews: (scopeKey: string, reviews: PrV2ReviewResponse[]) => void;
  stageOptimisticThreads: (scopeKey: string, reviewThreads: PrV2ReviewThread[]) => void;
  stageThreadResolutionOptimistic: (
    scopeKey: string,
    reviewThread: PrV2ReviewThread,
    resolved: boolean,
  ) => void;
  clearThreadResolutionOptimistic: (scopeKey: string, lookupIds: string[]) => void;
  linkCommentIds: (scopeKey: string, fromId: string, toId: string) => void;
  removeCommentsByLookupIds: (scopeKey: string, lookupIds: string[]) => void;
  removeReviewsByLookupIds: (scopeKey: string, lookupIds: string[]) => void;
  removeThreadsByLookupIds: (scopeKey: string, lookupIds: string[]) => void;
};

export const EMPTY_SCOPE: PrV2LocalScopeState = {
  nextCommentId: PR_V2_LOCAL_SCOPE_ID_START,
  nextReviewId: PR_V2_LOCAL_SCOPE_ID_START,
  nextThreadId: PR_V2_LOCAL_SCOPE_ID_START,
  commentsByClientId: {},
  commentAliasToClientId: {},
  commentOrder: [],
  reviewsByClientId: {},
  reviewAliasToClientId: {},
  reviewOrder: [],
  threadsByClientId: {},
  threadAliasToClientId: {},
  threadOrder: [],
};

export const EMPTY_VIEW: PrV2LocalCommentModelView = {
  comments: [],
  reviewThreads: [],
  commentReadModel: [],
  commentByLookupId: new Map(),
  readModelByLookupId: new Map(),
  commentByGithubId: new Map(),
  reviewByLookupId: new Map(),
  reviewThreadByCommentNodeId: new Map(),
};

export function ensureScope(scope: PrV2LocalScopeState | undefined): PrV2LocalScopeState {
  return scope ?? EMPTY_SCOPE;
}
